import SlickSlider, { Settings } from 'react-slick';
import styled from 'styled-components';
import { ThemeProps } from '../lib/theme';
import CaretUrl from '../public/icons/arrow.svg';
import { visuallyHidden } from './AccessibilityText';

interface Props extends Settings {
  fromTop?: string;
  customStyling?: any;
  blob?: string;
}

export const sliderStyle = ({
  theme,
  blob,
}: {
  theme: ThemeProps['theme'];
} & Pick<Props, 'blob'>) => `
  position: relative;
  user-select: none;
  touch-action: pan-y;
  -webkit-tap-highlight-color: transparent;
  padding: 0 0.5rem;

  .slick-list {
    position: relative;
    overflow: hidden;
    display: block;
    margin: 0;
    padding: 0;

    &:focus {
      outline: none;
    }

    &.dragging {
      cursor: pointer;
      cursor: hand;
    }
  }

  .slick-slider .slick-track,
  .slick-slider .slick-list {
    transform: translate3d(0, 0, 0);
  }

  .slick-track {
    position: relative;
    left: 0;
    top: 0;
    display: flex;
    margin-left: auto;
    margin-right: auto;

    &:before,
    &:after {
      content: '';
      display: table;
    }

    &:after {
      clear: both;
    }
  }

  &.slick-loading {
    .slick-slide,
    .slick-track {
      visibility: hidden;
    }
  }

  .slick-slide {
    float: left;
    height: 100%;
    min-height: 1px;
    display: none;

    > div {
      display: flex;
      justify-content: center;
      width: 100%;
    }

    img {
      display: block;
    }

    &.slick-loading img {
      display: none;
    }


    &.dragging img {
      pointer-events: none;
    }

    .slick-vertical & {
      display: block;
      height: auto;
      border: 1px solid transparent;
    }
  }

  .slick-arrow.slick-hidden {
    display: none;
  }

  &.slick-initialized .slick-slide {
    display: flex;
    height: auto;
  }

  ${
    blob
      ? `
      .slick-list {
        &:before {
          content: "";
          width: 100%;
          height: 100%;
          display: block;
          background: ${theme.colors.highlightBackground}; // fallback for invalid color strings
          background: ${blob};
          position: absolute;
          mask-image: url('/icons/blob-4.svg');
          mask-size: contain;
          mask-repeat: no-repeat;
          mask-position: center bottom;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }
      }`
      : ''
  }
`;

const dotsStyling = ({
  theme,
  customPaging,
}: ThemeProps & { customPaging: boolean }) => `
  .slick-dots {
    display: flex !important;
    justify-content: center;
    padding: 1rem 0;
    gap: ${customPaging ? 0.75 : 0.5}rem;

    ${
      customPaging
        ? `img {
          height: 50px;
          width: 50px;
          object-fit: contain;
          }`
        : `li {
            height: 0.5rem;
            width: 0.5rem;
            border-radius: 50%;
            background: ${theme.colors.faded};
            
            &.slick-active {
              background: ${theme.colors.highlightColor};
            }
            
            button {
              ${visuallyHidden}
            }
          }`
    }
  }`;

const arrowStyling = (fromTop?: string) => `
.slick-prev,
.slick-next {
  position: absolute;
  display: block;
  height: 1rem;
  width: 1rem;
  line-height: 0px;
  font-size: 0px;
  cursor: pointer;
  background: transparent;
  color: transparent;
  transform: translate(0, -50%);
  top: ${fromTop || 'calc((100% - 16px)/2)'};
  padding: 0;
  border: none;
  outline: none;
  z-index: ${({ theme }: ThemeProps) => theme.zIndex.default};


  &:hover,
  &:focus {
    outline: none;
    background: transparent;
    color: transparent;

    &:before {
      opacity: 1;
    }
  }

  &.slick-disabled:before {
    opacity: 0.25;
  }

  &:before {
    content: '';
    line-height: 1;
    opacity: .75;
    color: ${({ theme }: ThemeProps) => theme.colors.primaryColor};
    background: transparent url('${CaretUrl}') 50%/contain no-repeat;
    display: block;
    height: 100%;
    width: 100%;
  }
}

.slick-prev {
  left: -0.5rem;

  &:before {
    transform: rotate(180deg);
  }
}

.slick-next {
  right: -0.5rem;
}`;

const defaultSliderSettings = {
  accessibility: true,
  adaptiveHeight: true,
  infinite: true,
  dots: true,
  slidesToShow: 1,
  slidesToScroll: 1,
  cssEase: 'ease-in-out',
};

const Slider = styled(SlickSlider).attrs((p) => ({
  ...defaultSliderSettings,
  ...p,
}))<ThemeProps & Props>(
  ({
    theme,
    fromTop,
    customStyling,
    customPaging,
    blob,
  }: ThemeProps & Props) => `
  ${sliderStyle({ theme, blob })}
  ${dotsStyling({ theme, customPaging: !!customPaging })}
  ${arrowStyling(fromTop)}
  ${customStyling}
`,
);

export default Slider;
